import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  TrendingUp, BookOpenCheck, CheckCircle, AlertTriangle, Clock,
  ScanEye, PenLine, Users, Star, AlertCircle, ChevronRight,
} from 'lucide-react';
import { plannerAPI } from '../services/api';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, color = 'blue', sub, highlight }) {
  const colors = {
    blue:   'bg-blue-50 text-blue-600 border-blue-100',
    green:  'bg-green-50 text-green-600 border-green-100',
    amber:  'bg-amber-50 text-amber-600 border-amber-100',
    red:    'bg-red-50 text-red-600 border-red-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    teal:   'bg-teal-50 text-teal-600 border-teal-100',
  };
  return (
    <div className={`bg-white rounded-xl border p-4 flex items-start gap-3 ${highlight ? 'ring-2 ring-primary-300' : 'border-slate-200'}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${colors[color]}`}>
        <Icon size={18}/>
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 font-medium truncate">{label}</p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">{value ?? '—'}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function ComplianceBar({ pct, showLabel = true }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all ${pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showLabel && (
        <span className={`text-xs font-bold w-9 text-right ${pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
          {pct}%
        </span>
      )}
    </div>
  );
}

const STATUS_COLORS = {
  draft: '#94a3b8', submitted: '#3b82f6', under_review: '#f59e0b',
  revision_requested: '#f97316', coordinator_approved: '#14b8a6',
  vp_approved: '#22c55e', rejected: '#ef4444', completed: '#a855f7',
};
const STATUS_LABELS = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review',
  revision_requested: 'Revision Req.', coordinator_approved: 'Coord. Approved',
  vp_approved: 'VP Approved', rejected: 'Rejected', completed: 'Completed',
};

// ─── Teacher View ──────────────────────────────────────────────
function TeacherDashboard({ data }) {
  const { plans, compliance, observations, reflections, recent_plans } = data;

  const pieData = Object.entries(plans.by_status || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, fill: STATUS_COLORS[k] || '#94a3b8' }));

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Total Plans"    value={plans.total || 0}           icon={BookOpenCheck} color="blue"/>
        <KpiCard label="Compliance"     value={`${compliance.pct || 0}%`}  icon={TrendingUp}   color={compliance.pct >= 80 ? 'green' : compliance.pct >= 60 ? 'amber' : 'red'}
          sub={`${compliance.submitted || 0} of ${compliance.total || 0} submitted`} highlight={compliance.pct < 60}/>
        <KpiCard label="VP Approved"    value={(plans.by_status?.vp_approved || 0) + (plans.by_status?.completed || 0)} icon={CheckCircle} color="green"/>
        <KpiCard label="Late Submissions" value={plans.late || 0}           icon={AlertTriangle} color={plans.late > 0 ? 'red' : 'green'}/>
        <KpiCard label="Obs. Avg Score" value={observations?.avg_score ? `${observations.avg_score}%` : '—'} icon={ScanEye} color="purple"
          sub={`${observations?.total || 0} observation${observations?.total !== 1 ? 's' : ''}`}/>
        <KpiCard label="Reflections"    value={reflections || 0}           icon={PenLine} color="teal"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Status breakdown pie */}
        {pieData.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h4 className="text-sm font-bold text-slate-700 mb-4">Plan Status Breakdown</h4>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]}/>
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Recent plans */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-bold text-slate-700 mb-3">Recent Plans</h4>
          {recent_plans?.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No plans yet</p>
          ) : (
            <div className="space-y-2">
              {(recent_plans || []).map(p => (
                <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.lesson_title}</p>
                    <p className="text-xs text-slate-400">{p.subject_name} · W{p.week_number}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                    STATUS_COLORS[p.status] ? '' : ''
                  }`} style={{ background: `${STATUS_COLORS[p.status]}20`, color: STATUS_COLORS[p.status] }}>
                    {STATUS_LABELS[p.status] || p.status}
                  </span>
                  {p.is_late_submission ? <span className="text-xs text-red-400 flex-shrink-0">⚠</span> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Observation pending alert */}
      {observations?.pending_ack > 0 && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5"/>
          <div>
            <p className="text-sm font-semibold text-amber-800">Pending Observation Acknowledgement</p>
            <p className="text-xs text-amber-600 mt-0.5">You have {observations.pending_ack} observation{observations.pending_ack > 1 ? 's' : ''} awaiting your acknowledgement. Go to the Observations tab to review and acknowledge.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Coordinator View ──────────────────────────────────────────
function CoordinatorDashboard({ data }) {
  const { grades_compliance = [], pending_reviews, overdue_reviews, observations, teachers = [] } = data;

  const avgCompliance = grades_compliance.length
    ? Math.round(grades_compliance.reduce((s, g) => s + g.compliance_pct, 0) / grades_compliance.length)
    : 0;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Avg Compliance"    value={`${avgCompliance}%`}     icon={TrendingUp}    color={avgCompliance >= 80 ? 'green' : avgCompliance >= 60 ? 'amber' : 'red'}/>
        <KpiCard label="Pending Reviews"   value={pending_reviews || 0}    icon={Clock}         color={pending_reviews > 0 ? 'amber' : 'green'}/>
        <KpiCard label="Overdue Reviews"   value={overdue_reviews || 0}    icon={AlertTriangle} color={overdue_reviews > 0 ? 'red' : 'green'}
          sub={overdue_reviews > 0 ? '>24h SLA breach' : 'All within SLA'} highlight={overdue_reviews > 0}/>
        <KpiCard label="Observations Done" value={observations?.total || 0} icon={ScanEye}       color="purple"
          sub={observations?.avg_score ? `Avg ${observations.avg_score}%` : undefined}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Grade compliance bar chart */}
        {grades_compliance.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h4 className="text-sm font-bold text-slate-700 mb-4">Grade-wise Compliance</h4>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={grades_compliance} margin={{ top: 4, right: 10, bottom: 4, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="grade" tick={{ fontSize: 11 }}/>
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }}/>
                <Tooltip formatter={v => [`${v}%`, 'Compliance']}/>
                <Bar dataKey="compliance_pct" radius={[4, 4, 0, 0]}>
                  {grades_compliance.map((g, i) => (
                    <Cell key={i} fill={g.compliance_pct >= 80 ? '#22c55e' : g.compliance_pct >= 60 ? '#f59e0b' : '#ef4444'}/>
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Grade table */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-bold text-slate-700 mb-3">Grade Summary</h4>
          {grades_compliance.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No grades assigned</p>
          ) : (
            <div className="space-y-3">
              {grades_compliance.map(g => (
                <div key={g.grade}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-700">{g.grade}</span>
                    <span className="text-slate-400">{g.submitted}/{g.total} plans · {g.late || 0} late</span>
                  </div>
                  <ComplianceBar pct={g.compliance_pct}/>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Teacher compliance table */}
      {teachers.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
            <Users size={14} className="text-primary-500"/> Teacher Compliance Summary
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Teacher</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Plans</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Submitted</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Late</th>
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">Compliance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {teachers.map(t => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-medium text-slate-800">{t.name}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{t.total}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{t.submitted}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={t.late > 0 ? 'text-red-500 font-semibold' : 'text-slate-400'}>{t.late || 0}</span>
                    </td>
                    <td className="px-3 py-2.5 w-36"><ComplianceBar pct={t.compliance_pct}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin / VP View ───────────────────────────────────────────
function AdminDashboard({ data }) {
  const { totals = {}, grades_compliance = [], observations = {}, coordinators = [] } = data;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="School Compliance" value={`${totals.school_compliance || 0}%`} icon={TrendingUp}
          color={totals.school_compliance >= 80 ? 'green' : totals.school_compliance >= 60 ? 'amber' : 'red'}
          highlight={totals.school_compliance < 60}/>
        <KpiCard label="Total Plans"     value={totals.total || 0}            icon={BookOpenCheck} color="blue"/>
        <KpiCard label="Approved"        value={totals.approved || 0}          icon={CheckCircle}   color="green"
          sub={`of ${totals.submitted || 0} submitted`}/>
        <KpiCard label="Pending Review"  value={totals.pending_review || 0}    icon={Clock}         color={totals.pending_review > 5 ? 'amber' : 'blue'}/>
        <KpiCard label="Pending VP"      value={totals.pending_approval || 0}  icon={AlertTriangle} color={totals.pending_approval > 3 ? 'amber' : 'teal'}/>
        <KpiCard label="Observations"    value={observations?.total || 0}      icon={ScanEye}       color="purple"
          sub={observations?.avg_score ? `Avg ${observations.avg_score}%` : undefined}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Grade compliance bar chart */}
        {grades_compliance.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h4 className="text-sm font-bold text-slate-700 mb-4">School-wide Grade Compliance</h4>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={grades_compliance} margin={{ top: 4, right: 10, bottom: 4, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="grade" tick={{ fontSize: 11 }}/>
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }}/>
                <Tooltip
                  formatter={(v, name) => name === 'compliance_pct' ? [`${v}%`, 'Compliance'] : [v, name]}
                  labelFormatter={l => `Grade: ${l}`}
                />
                <Bar dataKey="compliance_pct" name="compliance_pct" radius={[4, 4, 0, 0]}>
                  {grades_compliance.map((g, i) => (
                    <Cell key={i} fill={g.compliance_pct >= 80 ? '#22c55e' : g.compliance_pct >= 60 ? '#f59e0b' : '#ef4444'}/>
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Grade detail table */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-bold text-slate-700 mb-3">Grade Breakdown</h4>
          {grades_compliance.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-2 py-2 text-xs font-semibold text-slate-500">Grade</th>
                    <th className="text-right px-2 py-2 text-xs font-semibold text-slate-500">Total</th>
                    <th className="text-right px-2 py-2 text-xs font-semibold text-slate-500">Approved</th>
                    <th className="text-right px-2 py-2 text-xs font-semibold text-slate-500">Late</th>
                    <th className="px-2 py-2 text-xs font-semibold text-slate-500">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {grades_compliance.map(g => (
                    <tr key={g.grade} className="hover:bg-slate-50">
                      <td className="px-2 py-2 font-semibold text-slate-700">{g.grade}</td>
                      <td className="px-2 py-2 text-right text-slate-600">{g.total}</td>
                      <td className="px-2 py-2 text-right text-green-600 font-semibold">{g.approved || 0}</td>
                      <td className="px-2 py-2 text-right">
                        <span className={g.late > 0 ? 'text-red-500 font-semibold' : 'text-slate-400'}>{g.late || 0}</span>
                      </td>
                      <td className="px-2 py-2 w-28"><ComplianceBar pct={g.compliance_pct}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Coordinator efficiency */}
      {coordinators.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
            <Star size={14} className="text-amber-500"/> Coordinator Review Efficiency
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Coordinator</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Reviews Done</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Avg Time</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Within SLA</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">SLA Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {coordinators.map((c, i) => {
                  const slaRate = c.reviews_done > 0 ? Math.round((c.within_sla / c.reviews_done) * 100) : 0;
                  return (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-medium text-slate-800">{c.name}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600">{c.reviews_done}</td>
                      <td className="px-3 py-2.5 text-right text-slate-500">{c.avg_hrs ? `${c.avg_hrs}h` : '—'}</td>
                      <td className="px-3 py-2.5 text-right text-green-600 font-semibold">{c.within_sla || 0}</td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                          ${slaRate >= 80 ? 'bg-green-100 text-green-700' : slaRate >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {slaRate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Export ───────────────────────────────────────────────
export default function LessonPlannerDashboard({ role, yearId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    plannerAPI.getDashboard({ academic_year_id: yearId || 3 })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [yearId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="py-16 flex items-center justify-center">
      <div className="flex items-center gap-3 text-slate-400">
        <div className="w-5 h-5 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"/>
        Loading dashboard…
      </div>
    </div>
  );

  if (!data) return null;

  const viewRole = data.role || role;

  return (
    <div>
      {viewRole === 'teacher'     && <TeacherDashboard data={data}/>}
      {viewRole === 'coordinator' && <CoordinatorDashboard data={data}/>}
      {viewRole === 'admin'       && <AdminDashboard data={data}/>}
    </div>
  );
}
